import React, { useEffect, useState } from 'react';
import { Box, Button, Grid, Typography } from '@mui/material';
import Container from '../../components/LandingPage/Container';
import { CheckCircleOutlineOutlined } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { getCampaignInfo } from '../../services/campaign-service';

const VantageBullet: React.FC<{ title: string }> = ({ title }) => {
  return (
    <Box display="flex" flexDirection="row" gap="11px" justifyContent="center">
      <CheckCircleOutlineOutlined width="24px" height="24px" sx={{ color: 'var(--acer-green)' }} />
      <Typography
        fontSize="16px"
        fontWeight="500"
        lineHeight="24px"
        letterSpacing="0.15px"
        color="white"
      >
        {title}
      </Typography>
    </Box>
  );
};

const defaultHeroContent = {
  image: '/images/landing-page-chat.jpeg',
  title: 'Desempenho confiável com Acer',
  description:
    'Escolha dispositivos Acer com performance moderna, qualidade reconhecida e excelente custo-benefício para seu dia a dia.',
  subtitle: 'Tecnologia que acompanha você',
};

const Hero: React.FC = () => {
  const { identifier: templateIdentifier } = useParams<{
    identifier?: string;
  }>();
  const { enqueueSnackbar } = useSnackbar();
  const [heroContent, setHeroContent] = useState(defaultHeroContent);

  useEffect(() => {
    let isMounted = true;

    const loadCampaignInfo = async () => {
      if (!templateIdentifier) {
        setHeroContent(defaultHeroContent);
        return;
      }

      try {
        const campaignInfo = await getCampaignInfo(templateIdentifier);

        if (!isMounted) {
          return;
        }

        setHeroContent({
          image: campaignInfo.promotional_image,
          title: campaignInfo.copywriting_title,
          subtitle: campaignInfo.copywriting_subtitle,
          description: campaignInfo.copywriting_description,
        });
      } catch (error) {
        if (!isMounted) {
          return;
        }

        enqueueSnackbar(
          'Não foi possível carregar o template da campanha. Exibindo conteúdo padrão.',
          { variant: 'error' }
        );
      }
    };

    loadCampaignInfo();

    return () => {
      isMounted = false;
    };
  }, [templateIdentifier, enqueueSnackbar]);

  const { image, title, subtitle, description } = heroContent;

  const vantageBullets = [
    'Notebooks e monitores para trabalho, estudo e entretenimento.',
    'Modelos atualizados com ótimo equilíbrio entre desempenho e preço.',
  ];

  return (
    <Box
      sx={{
        background: `radial-gradient(circle at center, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.6) 35%, rgba(0, 0, 0, 0.0) 70%), linear-gradient(90deg, rgb(20, 20, 20) 0%, rgba(20, 20, 20, 0.85) 35%, rgba(217, 217, 217, 0) 65%), linear-gradient(270deg, rgb(20, 20, 20) 0%, rgba(20, 20, 20, 0.85) 35%, rgba(217, 217, 217, 0) 65%), url(${image})`,
        backgroundRepeat: 'no-repeat, no-repeat, no-repeat, no-repeat',
        backgroundPosition: 'center center, left center, right center, center center',
        backgroundSize: 'cover, 50% 100%, 50% 100%, cover',
      }}
      id="campaign"
      height="calc(100vh - 83px)"
      minHeight="700px"
      display="flex"
    >
      <Container>
        <Box display="flex" flexDirection="column" gap="29px" width="100%" margin="auto">
          <Box display="flex" flexDirection="column" gap="24px" width="100%" textAlign="center">
            <Typography
              color="white"
              fontSize={{ xs: '21px', md: '48px' }}
              fontWeight={{ xs: '400', md: '700' }}
              lineHeight={{ xs: '31px', md: '1' }}
            >
              {title}
            </Typography>
            <Typography
              color="white"
              fontSize={{ xs: '16px', md: '21px' }}
              fontWeight={{ xs: '500', md: '400' }}
              lineHeight={{ xs: '24px', md: '31px' }}
              letterSpacing={{ xs: '0.15px', md: '0.666px' }}
            >
              {description}
            </Typography>
            <Typography
              color="white"
              fontSize="21px"
              fontWeight="700"
              lineHeight="31px"
              letterSpacing="0.666px"
            >
              {subtitle}
            </Typography>
          </Box>
          <Box
            display="flex"
            flexDirection={{ xs: 'column', md: 'row' }}
            gap="29px"
            justifyContent="center"
          >
            {vantageBullets.map((vantageBullet, index) => (
              <VantageBullet key={`${vantageBullet}-${index}`} title={vantageBullet} />
            ))}
          </Box>
          <Box display="flex" justifyContent="center">
            <Button
              variant="contained"
              sx={{ backgroundColor: 'var(--acer-green)' }}
              onClick={() =>
                document.getElementById('chat')?.scrollIntoView({ behavior: 'smooth' })
              }
            >
              <Typography color="white">Fale com nosso assistente e descubra mais</Typography>
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Hero;
